import SearchContainer from 'components/layout/header/SearchContainer';
import Link from 'next/link';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { UserDropdown } from 'components/user';
import ImpersonationBanner from 'components/kit/shell/ImpersonationBanner';
// import store from 'store';
import { useTranslation } from 'react-i18next';
import { GetTaskDasboard } from 'features/dashboard/services/TaskDasboardServices';
import { TaskType } from 'features/dashboard/types';
import moment from 'moment';
import { usePathname, useRouter } from 'next/navigation';
import IndexChat from 'features/chat/components/IndexChat';
import { useAuthContext } from 'contexts/AuthContext';
import { useSignalR } from 'contexts/SignalRProvider';
import { GetChatUnreadCount } from 'features/chat/services/chatServices';
import { ChatUnreadCount } from 'features/chat/types/types';
import { hasPermission } from 'utils/permissions';
import { toast } from 'react-toastify';
import { getCountTaskByStatusType } from 'features/settings/services/categroyTaskService';
import clsx from 'clsx';
import LucideIcon from './LucideIcon';
import {
  Menu,
  ListChecks,
  MessageCircle,
  Bell,
  HelpCircle,
  CalendarDays,
  Clock,
  ChevronRight,
} from 'lucide-react';
import useFetchData from 'hooks/useFetch';
import {
  GetNotifications,
  MarkAsReadNotification,
} from 'features/profile/services/ReminderService';
import EditResponse from 'features/forms/components/FormResponses/EditResponse';
import HeaderNewRequestMenu from 'components/layout/header/HeaderNewRequestMenu';
import {
  MENU_PANEL_BODY_CLASSES,
  MENU_PANEL_CLASSES,
  MENU_PANEL_FOOTER_CLASSES,
  MENU_PANEL_HEADER_CLASSES,
  MENU_PANEL_TITLE_CLASSES,
  MENU_ROW_CLASSES,
  MENU_ROW_DESCRIPTION_CLASSES,
  MENU_ROW_LABEL_CLASSES,
  MENU_ROW_TRAILING_CLASSES,
} from 'components/layout/header/menuStyles';
import InfoDemandeDocument from 'features/documents/components/document/InfoDemandeDocument';
import { LeaveDetails } from 'features/conge/components';
import ExpenseDetails from 'features/expenses/components/ExpenseDetails';
import DetailResponse from 'features/forms/components/FormResponses/DetailResponse';
import Tippy from '@tippyjs/react';
import useCompanyLogo from 'hooks/useCompanyLogo';

interface HeaderProps {
  setIsSidenavOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Header: FC<HeaderProps> = ({ setIsSidenavOpen }) => {
  const { logoUrl, showCompanyLogo, hideCompanyLogo } = useCompanyLogo();
  const { openedSidebars, openSidebar, closeSidebar } = useSidebarManager();
  const { user } = useAuthContext();
  const pathname = usePathname();
  useEffect(() => {
    if (user.isConnectToUser) {
      document.querySelector('body')?.classList.add('minthr-logged-in');
    } else {
      document.querySelector('body')?.classList.remove('minthr-logged-in');
    }
  }, [user.isConnectToUser]);
  const { t } = useTranslation();
  const router = useRouter();
  const tasksRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const [tasks, setTasks] = useState<TaskType[]>([]);
  const [showTasks, setShowTasks] = useState<boolean>(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const [tasksCount, setTasksCount] = useState<number>(0);
  const [chatUnreadCount, setChatUnreadCount] = useState<number>(0);
  const { connection, isConnected } = useSignalR();
  const isPolicyPage = pathname?.toLowerCase().includes('termsacceptance');

  const canViewChat =
    hasPermission('Chat.View') && user.chatAccessLevel !== 'DisableChat';

  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => setIsMobileSearchOpen(true);
    const handleClose = () => setIsMobileSearchOpen(false);
    window.addEventListener('open-mobile-search', handleOpen);
    window.addEventListener('close-mobile-search', handleClose);
    return () => {
      window.removeEventListener('open-mobile-search', handleOpen);
      window.removeEventListener('close-mobile-search', handleClose);
    };
  }, []);

  const fetchTasksCount = async () => {
    try {
      const response = await getCountTaskByStatusType(0);
      if (response) {
        setTasksCount(response);
      }
    } catch (error) {
      console.error('Failed to fetch tasks count', error);
    }
  };

  const fetchTasks = async (): Promise<void> => {
    const response = await GetTaskDasboard();
    if (response.success) {
      setTasks(response.data);
    } else {
      toast.error(response.message);
    }
  };

  const { data: notificationsData, refetch: refetchNotifs } = useFetchData(
    GetNotifications,
    [],
    { enabled: hasPermission('Notifications.View') },
  );

  useEffect(() => {
    if (hasPermission('Task.View') || hasPermission('Task.Admin.View')) {
      fetchTasks();
      fetchTasksCount();
    }
  }, []);

  useEffect(() => {
    if (!canViewChat || !isConnected || !connection) return;

    GetChatUnreadCount()
      .then((response) => {
        if (response.success) {
          setChatUnreadCount(response.data.total);
        } else {
          console.error(response.message, 'Failed to fetch chat unread count');
        }
      })
      .catch((err) => console.error('Failed to fetch chat unread count', err));

    const handler = (data: ChatUnreadCount) => {
      setChatUnreadCount(data.total);
    };
    connection.on('ChatUnreadCountUpdated', handler);

    return () => {
      connection.off('ChatUnreadCountUpdated', handler);
    };
  }, [canViewChat, isConnected, connection]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (tasksRef.current && !tasksRef.current.contains(target)) {
        setShowTasks(false);
      }

      if (notifRef.current && !notifRef.current.contains(target)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isChatOpen = openedSidebars.some(
    (s) => s.component === IndexChat && !s.isClosing,
  );

  return (
    <>
      {user.isConnectToUser && !isPolicyPage && (
        <ImpersonationBanner
          fullname={user.fullname}
          className="tw-fixed tw-top-0 tw-inset-x-0 tw-z-[99]"
        />
      )}

      <header
        className={`tw-fixed tw-end-0 tw-start-[52px] mobile:tw-start-0 tw-h-[44px] tw-bg-white tw-z-[100] tw-flex tw-items-center tw-border-b-[0.5px] tw-border-gray-200 tw-text-gray-800 tw-transition-all ${user.isConnectToUser ? 'tw-top-6' : 'tw-top-0'}`}
      >
        <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-between tw-px-[16px] tw-border-b-[0.5px] tw-border-gray-200">
          <div className="tw-hidden mobile:tw-flex tw-items-center">
            <Tippy content={t('Menu')}>
              <button
                onClick={() => setIsSidenavOpen(true)}
                className="tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[40px] tw-rounded-md tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors tw-duration-150"
              >
                <Menu size={16} strokeWidth={1.8} />
              </button>
            </Tippy>
          </div>

          {/* Logo slot — same style as the kit TopHeader's logo slot
            (`flex items-center gap-[8px] pe-[16px] shrink-0`) filled the way
            MinthrAppShell fills it, so both shells render an identical brand. */}
          <Link
            href="/"
            className={clsx(
              'tw-flex tw-items-center tw-gap-[8px] tw-pe-[16px] tw-shrink-0 tw-min-w-0 tw-no-underline tw-text-inherit hover:tw-text-inherit',
              isMobileSearchOpen && 'mobile:tw-hidden',
            )}
          >
            {showCompanyLogo && logoUrl ? (
              <img
                className="tw-h-[20px] tw-w-auto tw-object-contain"
                src={logoUrl}
                alt="Client"
                onError={hideCompanyLogo}
              />
            ) : (
              <span className="tw-text-[15px] tw-font-medium tw-text-gray-900 tw-truncate tw-max-w-[120px] sm:tw-max-w-none">
                {user.client}
              </span>
            )}
          </Link>

          <div className="tw-flex tw-items-center tw-gap-[8px] tw-flex-1 tw-justify-end">
            <div
              className={clsx(
                'tw-flex',
                isMobileSearchOpen && 'mobile:tw-flex-1 mobile:tw-w-full',
              )}
            >
              <SearchContainer />
            </div>
            <div
              className={clsx(
                'tw-flex tw-items-center tw-gap-[8px]',
                isMobileSearchOpen && 'mobile:tw-hidden',
              )}
            >
              {(hasPermission('Task.View') ||
                hasPermission('Task.Admin.View')) && (
                <div
                  className="tw-relative tw-flex tw-items-center"
                  ref={tasksRef}
                >
                  <Tippy content={t('Tasks')}>
                    <button
                      onClick={() => setShowTasks(!showTasks)}
                      className={clsx(
                        'tw-relative tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 tw-transition-colors tw-duration-150',
                        showTasks
                          ? 'tw-bg-gray-100 tw-text-gray-900'
                          : 'hover:tw-bg-gray-50 hover:tw-text-gray-900',
                      )}
                    >
                      <ListChecks size={16} strokeWidth={1.8} />
                      {(tasksCount > 0 || tasks.length > 0) && (
                        <span className="tw-absolute tw-top-[1px] tw-end-[1px] tw-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-[16px] tw-rounded-full tw-bg-danger-500 tw-text-[9px] tw-font-medium tw-leading-none tw-text-white">
                          {tasksCount || tasks.length}
                        </span>
                      )}
                    </button>
                  </Tippy>

                  {showTasks && (
                    <div
                      className={clsx(
                        MENU_PANEL_CLASSES,
                        // Full-bleed under the header on mobile.
                        'mobile:tw-fixed mobile:tw-top-[52px] [.minthr-logged-in_&]:mobile:tw-top-[calc(var(--unit-minthr)*30_+_52px)] mobile:tw-start-[8px] mobile:tw-end-[8px] mobile:tw-w-auto mobile:tw-mt-0',
                      )}
                    >
                      <div className={MENU_PANEL_HEADER_CLASSES}>
                        <span className={MENU_PANEL_TITLE_CLASSES}>
                          {t('nav.Tache')}
                        </span>
                      </div>

                      <div
                        className={clsx(
                          MENU_PANEL_BODY_CLASSES,
                          'mobile:tw-max-h-[55vh]',
                        )}
                      >
                        {tasks && tasks.length > 0 ? (
                          tasks.map((task) => {
                            return (
                              // Row — kit `TopHeaderMenuItem` (label + description).
                              <button
                                key={task.id}
                                onClick={() => {
                                  setShowTasks(false);
                                  router.push(
                                    `/tasks/${task.codeCategory}/${task.id}`,
                                  );
                                }}
                                className={MENU_ROW_CLASSES}
                              >
                                <span className="tw-flex-1 tw-min-w-0">
                                  <span className={MENU_ROW_LABEL_CLASSES}>
                                    {task.title}
                                  </span>
                                  <span
                                    className={MENU_ROW_DESCRIPTION_CLASSES}
                                  >
                                    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-[12px] tw-gap-y-[4px]">
                                      {task.endDate && (
                                        <span className="tw-inline-flex tw-items-center tw-gap-[4px]">
                                          <CalendarDays
                                            size={13}
                                            strokeWidth={1.8}
                                          />
                                          {moment(task.endDate).format(
                                            'DD MMM YYYY',
                                          )}
                                        </span>
                                      )}
                                      {task.remainingTime
                                        ?.dateRepresentation && (
                                        <span
                                          className={clsx(
                                            'tw-inline-flex tw-items-center tw-gap-[4px]',
                                            task.remainingTime.isObsolute &&
                                              'tw-text-danger-700',
                                          )}
                                        >
                                          <Clock size={13} strokeWidth={1.8} />
                                          {`${task.remainingTime.duration} ${t(
                                            task.remainingTime
                                              .dateRepresentation,
                                          )}`}
                                        </span>
                                      )}
                                    </span>
                                  </span>
                                </span>
                              </button>
                            );
                          })
                        ) : (
                          <div className="tw-py-[32px] tw-px-[16px] tw-text-center tw-text-[13px] tw-text-gray-500">
                            {t('NoTask.Affected')}
                          </div>
                        )}
                      </div>

                      <div className={MENU_PANEL_FOOTER_CLASSES}>
                        <Link
                          href="/tasks/grid"
                          onClick={() => setShowTasks(false)}
                          className={clsx(MENU_ROW_CLASSES, 'tw-no-underline')}
                        >
                          <span className="tw-flex-1 tw-min-w-0">
                            <span className={MENU_ROW_LABEL_CLASSES}>
                              {t('Permission.Task.Member.View.AllTask')}
                            </span>
                          </span>
                          <span className={MENU_ROW_TRAILING_CLASSES}>
                            <ChevronRight size={14} strokeWidth={1.8} />
                          </span>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="tw-hidden">
                <Link
                  href="/"
                  className="hover:tw-scale-110 active:tw-scale-90 tw-transition-transform tw-duration-200 tw-flex"
                  title={t('Home')}
                >
                  <img
                    src="/icons/home.svg"
                    alt={t('Home')}
                    className="tw-brightness-0 tw-invert"
                  />
                </Link>
              </div>

              {/* Chat */}
              {canViewChat && (
                <div
                  className="tw-relative tw-hidden md:tw-block"
                  ref={chatRef}
                >
                  <Tippy content={t('Feature.Title.ChatMessages')}>
                    <button
                      id="minthr-chat-btn-open"
                      onClick={() =>
                        isChatOpen
                          ? closeSidebar(IndexChat)
                          : openSidebar(IndexChat, true)
                      }
                      className={clsx(
                        'tw-relative tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 tw-transition-colors tw-duration-150',
                        isChatOpen
                          ? 'tw-bg-gray-100 tw-text-gray-900'
                          : 'hover:tw-bg-gray-50 hover:tw-text-gray-900',
                      )}
                    >
                      <MessageCircle size={16} strokeWidth={1.8} />
                      {chatUnreadCount > 0 && (
                        <span className="tw-absolute tw-top-[1px] tw-end-[1px] tw-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-[16px] tw-rounded-full tw-bg-danger-500 tw-text-[9px] tw-font-medium tw-leading-none tw-text-white">
                          {chatUnreadCount}
                        </span>
                      )}
                    </button>
                  </Tippy>
                </div>
              )}

              {/* Notifications */}
              {hasPermission('Notifications.View') && (
                <div
                  className="tw-relative tw-hidden md:tw-block"
                  ref={notifRef}
                >
                  <Tippy content={t('Notifications')}>
                    <button
                      onClick={() =>
                        setIsNotificationsOpen(!isNotificationsOpen)
                      }
                      className={clsx(
                        'tw-relative tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 tw-transition-colors tw-duration-150',
                        isNotificationsOpen
                          ? 'tw-bg-gray-100 tw-text-gray-900'
                          : 'hover:tw-bg-gray-50 hover:tw-text-gray-900',
                      )}
                    >
                      <Bell size={16} strokeWidth={1.8} />
                      {notificationsData && notificationsData.length > 0 && (
                        <span className="tw-absolute tw-top-[1px] tw-end-[1px] tw-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-[16px] tw-rounded-full tw-bg-danger-500 tw-text-[9px] tw-font-medium tw-leading-none tw-text-white">
                          {notificationsData.length}
                        </span>
                      )}
                    </button>
                  </Tippy>

                  {isNotificationsOpen && (
                    <div className="tw-absolute tw-top-full tw-end-0 tw-mt-[8px] tw-w-[340px] sm:tw-w-96 tw-bg-white tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] tw-border-[0.5px] tw-border-gray-200 tw-overflow-hidden tw-z-[200] tw-py-0 tw-animate-in tw-fade-in tw-zoom-in-95 tw-slide-in-from-top-2 tw-duration-200 tw-text-gray-700">
                      <div className="tw-px-[16px] tw-py-[10px] tw-bg-white tw-border-b-[0.5px] tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                        <span className="tw-text-[13px] tw-font-medium tw-text-gray-800 tw-leading-tight">
                          {t('Notifications')}
                        </span>

                        <span className="tw-px-[6px] tw-py-[2px] tw-rounded-[4px] tw-bg-gray-100 tw-text-gray-600 tw-text-[10px] tw-font-medium">
                          {notificationsData?.length || 0}
                        </span>
                      </div>

                      <div className="tw-max-h-[400px] tw-overflow-y-auto tw-custom-scrollbar">
                        {!notificationsData ||
                        notificationsData.length === 0 ? (
                          <div className="tw-p-12 tw-text-center">
                            <div className="tw-w-16 tw-h-16 tw-bg-gray-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4 tw-border tw-border-gray-100">
                              <LucideIcon
                                name="Bell"
                                size={24}
                                className="tw-text-gray-300"
                              />
                            </div>
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                              {t(
                                'dashboard.notif.allCaughtUp',
                                'Tout est à jour !',
                              )}
                            </p>
                            <p className="tw-text-s tw-text-gray-500 tw-mt-1">
                              {t(
                                'dashboard.notif.empty',
                                "Vous n'avez pas de nouvelles notifications.",
                              )}
                            </p>
                          </div>
                        ) : (
                          notificationsData.map((n) => (
                            <button
                              key={n.id}
                              onClick={async () => {
                                setIsNotificationsOpen(false);
                                // Integration with openNotification logic
                                const { sourceName, sourceId, sourceUserId } =
                                  n;
                                switch (sourceName) {
                                  case 'Anniversary':
                                  case 'HiringAnniversary':
                                    openSidebar(IndexChat, true, {
                                      userId: sourceUserId,
                                      isGroup: false,
                                    });
                                    break;
                                  case 'DocumentsRequest':
                                    openSidebar(InfoDemandeDocument, true, {
                                      id: sourceId,
                                    });
                                    break;
                                  case 'CollaboratorExpensesRequests':
                                    openSidebar(ExpenseDetails, true, {
                                      noteId: sourceId,
                                    });
                                    break;
                                  case 'CongeRequests':
                                  case 'CongeSoon':
                                    openSidebar(LeaveDetails, true, {
                                      id: sourceId,
                                    });
                                    break;
                                  case 'forms':
                                    openSidebar(EditResponse, false, {
                                      formId: sourceId,
                                      responseId: 0,
                                      userId: user.userId as string,
                                      withUser: true,
                                    });
                                    break;
                                  case 'ResponseForm':
                                    openSidebar(DetailResponse, true, {
                                      responseId: sourceId,
                                    });
                                    break;
                                  default:
                                    router.push(
                                      `/employee/${sourceUserId}/profile/public`,
                                    );
                                }
                                await MarkAsReadNotification(n.id);
                                refetchNotifs();
                              }}
                              className="tw-w-full tw-text-left tw-px-5 tw-py-4 hover:tw-bg-[#F2F9F6]/50 tw-transition-all tw-border-b tw-border-gray-50 tw-flex tw-gap-4 tw-items-start tw-group"
                            >
                              <div className="tw-p-2.5 tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 group-hover:tw-bg-primary/10 group-hover:tw-border-primary/20 tw-transition-colors">
                                {/* Icon fallback or based on type */}
                                <LucideIcon
                                  name="Bell"
                                  size="l"
                                  className="tw-text-gray-400 group-hover:tw-text-primary"
                                />
                              </div>
                              <div className="tw-flex-1">
                                <p className="tw-text-sm tw-font-bold tw-leading-tight tw-text-gray-900 group-hover:tw-text-primary tw-transition-colors">
                                  {t(n.title).replace(
                                    '{0}',
                                    n.parameterTitle?.toString() || '',
                                  )}
                                </p>
                                <p className="tw-text-s tw-text-gray-500 tw-mt-1.5 tw-leading-relaxed">
                                  {moment(n.createdDate).fromNow()}
                                </p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>

                      <div className="tw-px-2 tw-py-1.5 tw-bg-white tw-border-t tw-border-gray-50">
                        <button className="tw-w-full tw-text-[calc(var(--unit-minthr)*12)] tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-py-1.5 tw-rounded-lg tw-font-medium tw-text-primary hover:tw-bg-primary hover:tw-text-white hover:tw-shadow-md hover:tw-shadow-primary/30 active:tw-scale-[0.98] tw-transition-all tw-duration-300 tw-group">
                          {t('Paramètres des notifications')}
                          <LucideIcon
                            name="Settings"
                            size={14}
                            className="group-hover:tw-rotate-90 tw-transition-transform tw-duration-500"
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-[8px]">
              <HeaderNewRequestMenu />

              {!user.isConnectToUser && hasPermission('WorkItem.Access') && (
                <Tippy content={t('Feedback.Title')}>
                  <button
                    className="tw-hidden md:tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-50 hover:tw-text-gray-900 tw-transition-colors tw-duration-150"
                    id="minthr-feedback-btn-open"
                  >
                    <HelpCircle size={16} strokeWidth={1.8} />
                  </button>
                </Tippy>
              )}
            </div>

            <UserDropdown />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
